import React from 'react';
import { Button } from '../ui/button';
import LazyPaneRenderer from './LazyPaneRenderer';
import ResizablePane from './ResizablePane';
import MobileResizableCard from './MobileResizableCard';
import DraggablePaneWrapper from './DraggablePaneWrapper';
import DashboardGridBuilder from './DashboardGridBuilder';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';

/**
 * Renders one row of panes — used both for the single-row layout and for
 * each row of a multi-row `layoutGrid`. Internally keeps its own
 * drag-and-drop ordering and resize widths so rows reorder independently.
 *
 * Resize and DnD reordering only affect THIS row. Cross-row dragging is a
 * future enhancement — for now a pane is removed from its row via the X
 * button and re-added via the pane selector.
 */
const PaneRow = ({
  panes,
  availablePanes,
  paneWidths,
  selectedVerse,
  aiInsights,
  showAIInsights,
  isLoadingAI,
  onResize,
  onReorder,
  onRemove,
  onVerseChange,
  onAIAnalyze,
}) => {
  const {
    items: orderedRow,
    draggedItem,
    draggedOverItem,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDrop,
  } = useDragAndDrop(panes, onReorder);

  const widthFor = (paneId, idx, total) => {
    if (paneWidths[paneId]) return paneWidths[paneId];
    return total === 1 ? 100 : total === 2 ? 50 : 100 / total;
  };

  return (
    <div className="flex-1 min-h-0 flex gap-0 overflow-hidden">
      {orderedRow.map((paneId, idx) => {
        const label = availablePanes.find((p) => p.id === paneId)?.label || paneId;
        const isDragging = draggedItem?.index === idx;
        const isDraggedOver = draggedOverItem?.index === idx;
        const isLast = idx === orderedRow.length - 1;
        return (
          <ResizablePane
            key={paneId}
            paneId={paneId}
            initialWidth={widthFor(paneId, idx, orderedRow.length)}
            minWidth={15}
            maxWidth={85}
            onResize={onResize}
            isResizable={!isLast && orderedRow.length > 1}
            className="h-full"
          >
            <DraggablePaneWrapper
              paneId={paneId}
              paneLabel={label}
              isDragging={isDragging}
              isDraggedOver={isDraggedOver}
              onDragStart={(e) => handleDragStart(e, paneId, idx)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, paneId, idx)}
              onDrop={(e) => handleDrop(e, paneId, idx)}
              onRemove={onRemove}
              canRemove={panes.length > 1}
            >
              <LazyPaneRenderer
                paneId={paneId}
                selectedVerse={selectedVerse}
                aiInsights={aiInsights}
                showAIInsights={showAIInsights}
                onVerseChange={onVerseChange}
                onAIAnalyze={onAIAnalyze}
                isLoadingAI={isLoadingAI}
              />
            </DraggablePaneWrapper>
          </ResizablePane>
        );
      })}
    </div>
  );
};

/**
 * Top-level layout shell — picks the right render based on viewport
 * (mobile = stacked cards, desktop = drag/resize panes) AND whether the
 * user has a multi-row `layoutGrid` set.
 */
const DashboardLayoutShell = ({
  isMobile,
  layout,
  layoutGrid,
  availablePanes,
  paneWidths,
  selectedVerse,
  aiInsights,
  showAIInsights,
  isLoadingAI,
  language,
  onPaneResize,
  onPaneReorder,
  onPaneGridReorder,
  onRemovePane,
  onVerseChange,
  onAIAnalyze,
}) => {
  // Mobile — always vertical stacked cards (rows collapse to scroll list).
  if (isMobile) {
    return (
      <div className="h-full overflow-y-auto p-4 space-y-4" data-testid="dashboard-mobile-shell">
        {layout.map((paneId) => {
          const label = availablePanes.find((p) => p.id === paneId)?.label || paneId;
          return (
            <MobileResizableCard
              key={paneId}
              paneId={paneId}
              header={
                <div className="flex items-center justify-between mb-4 pb-2 border-b">
                  <h3 className="font-semibold text-lg">{label}</h3>
                  {layout.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemovePane(paneId)}
                      className="text-red-500 hover:text-red-700"
                      aria-label={language === 'es' ? 'Eliminar panel' : 'Remove pane'}
                    >
                      ✕
                    </Button>
                  )}
                </div>
              }
            >
              <LazyPaneRenderer
                paneId={paneId}
                selectedVerse={selectedVerse}
                aiInsights={aiInsights}
                showAIInsights={showAIInsights}
                onVerseChange={onVerseChange}
                onAIAnalyze={onAIAnalyze}
                isLoadingAI={isLoadingAI}
              />
            </MobileResizableCard>
          );
        })}
      </div>
    );
  }

  // Desktop multi-row grid — full DnD grid builder
  if (layoutGrid && layoutGrid.length > 0) {
    return (
      <DashboardGridBuilder
        layoutGrid={layoutGrid}
        availablePanes={availablePanes}
        paneWidths={paneWidths}
        selectedVerse={selectedVerse}
        aiInsights={aiInsights}
        showAIInsights={showAIInsights}
        isLoadingAI={isLoadingAI}
        onPaneResize={onPaneResize}
        onGridChange={(next) => onPaneGridReorder('full', next)}
        onRemovePane={onRemovePane}
        onVerseChange={onVerseChange}
        onAIAnalyze={onAIAnalyze}
      />
    );
  }

  // Desktop single-row (legacy + default)
  return (
    <div className="h-full flex gap-0 p-4 overflow-hidden pb-4" data-testid="dashboard-row-shell">
      <PaneRow
        panes={layout}
        availablePanes={availablePanes}
        paneWidths={paneWidths}
        selectedVerse={selectedVerse}
        aiInsights={aiInsights}
        showAIInsights={showAIInsights}
        isLoadingAI={isLoadingAI}
        onResize={onPaneResize}
        onReorder={onPaneReorder}
        onRemove={onRemovePane}
        onVerseChange={onVerseChange}
        onAIAnalyze={onAIAnalyze}
      />
    </div>
  );
};

export default DashboardLayoutShell;
